import React, { useEffect, useState } from "react";
import { SITE } from "../data/site";

/**
 * Luxury hero slider — crossfades between curated images.
 * Mobile-optimized: separate objectPosition for desktop vs mobile to keep
 * subjects (Capitol dome, Lincoln Memorial, etc.) properly framed in portrait.
 */
export default function HeroSlider() {
  const slides = [
    {
      src: SITE.heroFleet,
      alt: "US Capitol at night with light trails — Washington DC",
      desktopPos: "center 55%",
      mobilePos: "62% center", // focus right side so Capitol dome stays in frame
    },
    {
      src: SITE.heroMixed,
      alt: "Lincoln Memorial long exposure at night — Washington DC",
      desktopPos: "center 50%",
      mobilePos: "center 45%",
    },
    {
      src: SITE.heroCapitol,
      alt: "US Capitol Hill nighttime view — Washington DC",
      desktopPos: "center 55%",
      mobilePos: "55% 40%",
    },
  ];

  const [idx, setIdx] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(id);
  }, [slides.length]);

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="hero-slider">
      {slides.map((s, i) => (
        <div
          key={s.src}
          className="absolute inset-0 transition-opacity duration-[1400ms] ease-in-out"
          style={{ opacity: i === idx ? 1 : 0, zIndex: i === idx ? 1 : 0 }}
        >
          <img
            src={s.src}
            alt={s.alt}
            className="w-full h-full object-cover hero-kenburns"
            style={{ objectPosition: isMobile ? s.mobilePos : s.desktopPos }}
            loading={i === 0 ? "eager" : "lazy"}
            fetchpriority={i === 0 ? "high" : "low"}
          />
        </div>
      ))}

      {/* Overlay — stronger vertical gradient on mobile for legibility */}
      <div className="absolute inset-0 z-[2] hidden md:block bg-gradient-to-r from-[#050505]/85 via-[#050505]/55 to-[#050505]/15" />
      <div className="absolute inset-0 z-[2] md:hidden bg-gradient-to-b from-[#050505]/70 via-[#050505]/40 to-[#050505]/95" />
      <div className="absolute inset-0 z-[2] bg-gradient-to-t from-[#050505] via-transparent to-transparent" />

      {/* Slide indicators */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-[3] flex gap-2" data-testid="hero-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            aria-label={`Slide ${i + 1}`}
            onClick={() => setIdx(i)}
            className={`h-[3px] transition-all duration-500 ${i === idx ? "w-10 bg-[#d4af6a]" : "w-5 bg-white/30 hover:bg-white/50"}`}
            data-testid={`hero-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}
